<script lang="ts" setup>
import RequestsTable from '@components/RequestsTable/RequestsTable.vue';
import Card from '@components/Card/Card.vue';
import Comparison from '@components/Comparison/Comparison.vue';
import routes from '@routes';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

import dayjs, { ManipulateType, QUnitType } from 'dayjs';
import { useDossiersStore } from '@stores/useDossiersStore';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { DropDownOption } from '@graphql/types';

dayjs.extend(quarterOfYear);

const { setTotals } = useDossiersStore();
const { dossiersTotals } = toRefs(useDossiersStore());

const { t: translate } = useI18n();

definePageMeta({
  name: routes.myRequests,
  title: '/mijn-aanvragen.page-title',
  middleware: ['auth', 'requests'],
});

const pastDateInDays = (type: ManipulateType | QUnitType) => {
  const currentDate = dayjs();
  const previousDate = dayjs().add(-1, type as ManipulateType);

  return currentDate.diff(previousDate, 'day');
};

const dayFilterOptions: DropDownOption[] = [
  {
    label: translate('/mijn-aanvragen.day-filter-month'),
    value: pastDateInDays('M').toString(),
  },
  {
    label: translate('/mijn-aanvragen.day-filter-quarter'),
    value: pastDateInDays('Q').toString(),
  },
  {
    label: translate('/mijn-aanvragen.day-filter-year'),
    value: pastDateInDays('y').toString(),
  },
];

const selectedRequestFilter = ref<UIDropDownOption>(dayFilterOptions[0]);
const selectedLoanFilter = ref<UIDropDownOption>(dayFilterOptions[0]);

const fetchTotals = () => setTotals(selectedRequestFilter.value.value, selectedLoanFilter.value.value);

onMounted(() => {
  fetchTotals();
});
</script>

<template>
  <NuxtLayout name="sidebar" :title="translate('/mijn-aanvragen.page-title')">
    <div class="flex flex-col w-full grow">
      <div class="relative tablet:hidden flex mt-4 mb-6">
        <UIButton
          class="w-full"
          @click="$router.push({name: routes.quickscan})"
        >
          {{ translate('/header.quickscan-button') }}
          <template #icon-end>
            <FontAwesomeIcon :icon="['fas', 'arrow-right']" />
          </template>
        </UIButton>
      </div>
      <div v-if="dossiersTotals" class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card :title="translate('/mijn-aanvragen.new-requests-title')" icon="file-circle-plus">
          <div class="flex flex-col 2xl:!flex-row gap-8">
            <Comparison
              :current-value="dossiersTotals.new_dossiers.current.toString()"
              :comparison="dossiersTotals.new_dossiers.comparison"
            />
            <div class="mt-auto ml-auto w-52">
              <UIDropDown
                name="new-requests-status-filter"
                aria-label="Filter op status"
                :options="dayFilterOptions"
                :model-value="selectedRequestFilter"
                data-test-id="intermediair-dropdown-aanvragen"
                @update:model-value="selectedRequestFilter = $event, fetchTotals()"
              />
            </div>
          </div>
        </Card>
        <Card title="Totale leensom" icon="sack">
          <div class="flex flex-col 2xl:!flex-row gap-8 justify-items-end">
            <Comparison
              :current-value="formatMoney(parseFloat(dossiersTotals.loan_amount.current))"
              :comparison="dossiersTotals.loan_amount.comparison"
            />
            <div class="ml-auto w-52">
              <UIDropDown
                name="total-loansum-period-filter"
                aria-label="Filter op periode"
                :options="dayFilterOptions"
                :model-value="selectedLoanFilter"
                data-test-id="intermediair-dropdown-totale-leensom"
                @update:model-value="selectedLoanFilter = $event, fetchTotals()"
              />
            </div>
          </div>
        </Card>
      </div>
      <div class="mt-4 flex-1 flex flex-col">
        <RequestsTable class="col-span-3" />
      </div>
    </div>
  </NuxtLayout>
</template>
